import { useCurrentEditor, useEditorState } from "@tiptap/react";
import { type ComponentProps, type FC, type RefObject, useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { useElementFromRef } from "../../../hooks";
import { Button } from "../../button";
import { ButtonGroup } from "../../button-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../dropdown-menu";
import { FieldCheckbox, FieldInput, Form, FormReset, FormSubmit } from "../../form";
import { Group } from "../../group";
import { Icon } from "../../icon";
import { Label } from "../../label";
import { EditorButton, type EditorButtonProps } from "./editor-button";

const validationSchema = z.object({
  text: z.string().min(1, "Please enter the link text"),
  href: z.string().url("Please enter a valid URL"),
  openInNewTab: z.boolean(),
});

type LinkFormValues = z.infer<typeof validationSchema>;

export interface EditorButtonLinkProps extends EditorButtonProps {
  containerRef?: RefObject<HTMLDivElement | null>;
}

export const EditorButtonLink: FC<EditorButtonLinkProps> = ({ containerRef, ...props }) => {
  const container = useElementFromRef(containerRef);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [linkDefaultValues, setLinkDefaultValues] = useState<LinkFormValues | null>(null);

  const { editor } = useCurrentEditor();
  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      canLink: ctx.editor?.can().chain().toggleLink().run() ?? false,
      isLink: ctx.editor?.isActive("link") || false,
      linkHref: ctx.editor?.getAttributes("link").href ?? null,
    }),
  });

  const handleOpenDialogChange = useCallback(
    (open: boolean) => {
      if (!editor) {
        return;
      }

      if (!open) {
        editor.chain().focus().extendMarkRange("link").run();
      }

      setIsEditOpen(open);
    },
    [editor]
  );

  const handleEdit = useCallback(() => {
    if (!editor) {
      return;
    }

    // Extend selection to cover the full link mark before extracting text
    editor.chain().focus().extendMarkRange("link").run();

    const { href, target } = editor?.getAttributes("link") ?? {};
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, " ");

    setLinkDefaultValues({
      text: text ?? "",
      href: href ?? "",
      openInNewTab: !target || target === "_blank" ? true : false,
    });
    setIsEditOpen(true);
  }, [editor]);

  const handleSubmit = useCallback(
    (values: LinkFormValues) => {
      if (!editor) {
        return;
      }

      if (!values.href) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        setLinkDefaultValues(null);
        setIsEditOpen(false);
        return;
      }

      // Store selection position before insertion
      editor.chain().focus().extendMarkRange("link").run();
      const { from } = editor.state.selection;

      // Delete existing content, insert new text, then apply link mark
      editor
        .chain()
        .deleteSelection()
        .insertContent(values.text)
        .setTextSelection({ from, to: from + values.text.length })
        .setLink({
          href: values.href,
          target: values.openInNewTab ? "_blank" : "_self",
        })
        .run();
      setLinkDefaultValues(values);
      setIsEditOpen(false);
    },
    [editor]
  );

  const handleRemove = useCallback(() => {
    if (!editor) {
      return;
    }

    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setLinkDefaultValues(null);
    setIsEditOpen(false);
  }, [editor]);

  if (!editor || !editorState) {
    return null;
  }

  return (
    <>
      {editorState.isLink && (
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <EditorButton {...props} icon="link" className="max-w-40">
              <span className="truncate">{editorState.linkHref}</span>
            </EditorButton>
          </DropdownMenuTrigger>

          {isDropdownOpen && (
            <DropdownMenuContent align="start" container={container}>
              <DropdownMenuItem onSelect={() => window.open(editorState.linkHref, "_blank")}>
                <Icon icon="open_in_new" />
                Visit link
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleEdit}>
                <Icon icon="edit" />
                Edit link
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleRemove}>
                <Icon icon="delete" />
                Remove link
              </DropdownMenuItem>
            </DropdownMenuContent>
          )}
        </DropdownMenu>
      )}

      {!editorState.isLink && (
        <EditorButton
          {...props}
          onClick={handleEdit}
          icon="link"
          tooltip="Insert link"
          disabled={!editorState.canLink}
        />
      )}

      {linkDefaultValues && (
        <EditorButtonLinkDialog
          isOpen={isEditOpen}
          onOpenChange={handleOpenDialogChange}
          onSubmit={handleSubmit}
          defaultValues={linkDefaultValues}
          onRemove={handleRemove}
        />
      )}
    </>
  );
};

interface EditorButtonLinkDialogProps extends ComponentProps<typeof Dialog> {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: LinkFormValues) => void;
  defaultValues: LinkFormValues;
  onRemove: () => void;
}

export const EditorButtonLinkDialog: FC<EditorButtonLinkDialogProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  defaultValues,
  onRemove,
  ...dialogProps
}) => (
  <Dialog {...dialogProps} open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Link Settings</DialogTitle>
        <DialogDescription>Enter the link text and URL.</DialogDescription>
      </DialogHeader>

      <Form onSubmit={onSubmit} defaultValues={defaultValues} validationSchema={validationSchema}>
        <Group className="gap-6">
          <Group>
            <FieldInput label="Text" name="text" placeholder="Enter the link text" />
            <Group className="gap-1.5">
              <Label className="text-xs text-muted-foreground font-semibold">URL</Label>
              <ButtonGroup className="items-start">
                <FieldInput name="href" placeholder="https://example.com" />
                <OpenLinkInNewTabButton />
              </ButtonGroup>
            </Group>
            <FieldCheckbox name="openInNewTab" label="Open in new tab" />
          </Group>

          <DialogFooter>
            {defaultValues?.href && (
              <Button type="button" variant="outline" onClick={onRemove}>
                <Icon icon="delete" />
                Remove link
              </Button>
            )}

            <span className="flex-1" />
            <FormReset onClick={() => onOpenChange(false)} />
            <FormSubmit />
          </DialogFooter>
        </Group>
      </Form>
    </DialogContent>
  </Dialog>
);

const OpenLinkInNewTabButton: FC<ComponentProps<typeof Button>> = (props) => {
  const { watch } = useFormContext();

  const href = watch("href") as string;
  const isValid = useMemo(() => validationSchema.pick({ href: true }).safeParse({ href }).success, [href]);

  const handleClick = useCallback(() => {
    if (!href) {
      return;
    }
    window.open(href, "_blank");
  }, [href]);

  return (
    <Button
      type="button"
      variant="outline"
      display="icon"
      onClick={handleClick}
      disabled={!isValid}
      {...props}
      className="text-lg"
    >
      <Icon icon="open_in_new" />
    </Button>
  );
};
